import { useMemo, useState } from 'react';
import { ListTable } from '@/components/Screen';
import { SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Form,
  Input,
  Loading,
  Select,
  StatusBadge,
} from '@/components/ui';
import { required, str } from '@/utils/format';

const PRICING_LABELS: Record<string, string> = {
  fixed: 'فئات ثابتة',
  variable: 'قيمة حرة',
};

const COMPANY_LABELS: Record<string, string> = {
  syriatel: 'سيريتل',
  mtn: 'MTN',
};

function groupKey(company: unknown, kind: unknown): string {
  return `${str(company)}::${str(kind)}`;
}

function emptyService(): JsonMap {
  return { id: 0, name: '', slug: '', pricing_mode: 'fixed', is_active: 1, sort_order: 0 };
}

/** USSD services + codes — lives on Executor page (production `Yr`). */
export function UssdCatalogPanels(props: { onMessage?: (msg: string) => void } = {}) {
  const { onMessage } = props;
  const state = useAsync(
    async () => {
      const [services, codes, devices] = await Promise.all([
        admin.ussdServices(),
        admin.ussdCodes(),
        admin.executorDevices(),
      ]);
      if (!services.success) return services;
      if (!codes.success) return codes;
      return {
        success: true as const,
        services: pickList(services, 'data', 'services', 'items'),
        codes: pickList(codes, 'data', 'codes', 'items'),
        devices: devices.success ? pickList(devices, 'data', 'devices', 'items') : [],
      };
    },
    (p) => ({
      services: Array.isArray(p.services) ? (p.services as JsonMap[]) : [],
      codes: Array.isArray(p.codes) ? (p.codes as JsonMap[]) : [],
      devices: Array.isArray(p.devices) ? (p.devices as JsonMap[]) : [],
    }),
  );

  const [serviceForm, setServiceForm] = useState<JsonMap>(() => emptyService());
  const [companyTab, setCompanyTab] = useState<'syriatel' | 'mtn'>('syriatel');
  const [codeDrafts, setCodeDrafts] = useState<Record<number, JsonMap>>({});
  const [selectedCodeId, setSelectedCodeId] = useState<Record<string, number>>({});
  const [newCode, setNewCode] = useState({
    operation_kind: '',
    name: '',
    code_template: '',
    executor_device_id: 0,
    is_active: 1,
  });
  const [msg, setMsgState] = useState('');
  const [busy, setBusy] = useState(false);

  function setMsg(next: string) {
    setMsgState(next);
    if (next) onMessage?.(next);
  }

  const services = state.data?.services ?? [];
  const codes = state.data?.codes ?? [];
  const devices = state.data?.devices ?? [];

  const activeServices = useMemo(
    () =>
      services.filter(
        (s) => Number(s.is_active) === 1 || codes.some((c) => str(c.operation_kind) === str(s.slug)),
      ),
    [services, codes],
  );

  function draftFor(id: number, row: JsonMap): JsonMap {
    return codeDrafts[id] ?? row;
  }

  function patchDraft(id: number, row: JsonMap, patch: JsonMap) {
    setCodeDrafts((prev) => ({ ...prev, [id]: { ...draftFor(id, row), ...patch } }));
  }

  async function saveService() {
    const miss = required({ name: str(serviceForm.name) }, ['name']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    const res = await admin.saveUssdService({
      id: serviceForm.id,
      name: serviceForm.name,
      slug: serviceForm.slug,
      pricing_mode: serviceForm.pricing_mode,
      is_active: serviceForm.is_active,
      sort_order: serviceForm.sort_order,
    });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم حفظ الخدمة.') : errorMessage(res));
    if (res.success) {
      setServiceForm(emptyService());
      state.reload();
    }
  }

  async function deleteService(row: JsonMap) {
    if (!window.confirm(`حذف فئة «${str(row.name)}» نهائياً مع رموز USSD التابعة لها؟`)) return;
    setBusy(true);
    const res = await admin.deleteUssdService({ id: row.id });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم الحذف.') : errorMessage(res));
    if (res.success) {
      if (Number(serviceForm.id) === Number(row.id)) setServiceForm(emptyService());
      state.reload();
    }
  }

  async function saveCode(id: number, row: JsonMap, makeDefault = false) {
    const body = draftFor(id, row);
    setBusy(true);
    const res = await admin.saveUssdCode({ ...body, id, make_default: makeDefault ? 1 : 0 });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم حفظ الكود.') : errorMessage(res));
    if (res.success) {
      setCodeDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      state.reload();
    }
  }

  async function deleteCode(row: JsonMap) {
    if (!window.confirm(`حذف ${str(row.name)} نهائياً؟`)) return;
    setBusy(true);
    const res = await admin.deleteUssdCode({ id: row.id });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم الحذف.') : errorMessage(res));
    if (res.success) state.reload();
  }

  async function addCode() {
    if (!newCode.operation_kind) {
      setMsg('اختر فئة الخدمة أولاً.');
      return;
    }
    setBusy(true);
    const res = await admin.saveUssdCode({ company: companyTab, ...newCode });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تمت العملية.') : errorMessage(res));
    if (res.success) {
      setNewCode({
        operation_kind: str(activeServices[0]?.slug, ''),
        name: '',
        code_template: '',
        executor_device_id: 0,
        is_active: 1,
      });
      state.reload();
    }
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <>
      {msg && !onMessage ? (
        <p className={msg.includes('تعذر') || msg.includes('يرجى') ? 'error' : 'ok'}>{msg}</p>
      ) : null}

      <SectionCard
        title="فئات خدمات USSD"
        subtitle="أضف خدمات التنفيذ التي تريد ظهورها في الواجهة، مثل وحدات أو فاتورة أو كاش أو جملة."
        icon={<Icon name="orders" size={24} />}
      >
        <Form onSubmit={() => void saveService()}>
          <div className="form-grid two-columns">
            <Input
              label="اسم الخدمة"
              value={str(serviceForm.name, '')}
              onChange={(e) => setServiceForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="مثال: جملة"
              required
            />
            <Select
              label="طريقة التسعير"
              value={str(serviceForm.pricing_mode, 'fixed')}
              onChange={(v) => setServiceForm((f) => ({ ...f, pricing_mode: v }))}
              options={[
                { value: 'fixed', label: 'فئات ثابتة' },
                { value: 'variable', label: 'قيمة حرة' },
              ]}
            />
            <Input
              label="الرمز الداخلي (اختياري)"
              value={str(serviceForm.slug, '')}
              onChange={(e) => setServiceForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="يُنشأ تلقائياً للخدمات الجديدة"
              disabled={Number(serviceForm.id) > 0}
            />
            <Select
              label="الحالة"
              value={String(Number(serviceForm.is_active ?? 1))}
              onChange={(v) => setServiceForm((f) => ({ ...f, is_active: Number(v) }))}
              options={[
                { value: '1', label: 'مفعّلة' },
                { value: '0', label: 'معطّلة' },
              ]}
            />
            <Input
              label="الترتيب"
              type="number"
              value={String(serviceForm.sort_order ?? 0)}
              onChange={(e) => setServiceForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
            />
          </div>
          <div className="action-row">
            <Button type="submit" disabled={busy}>
              {Number(serviceForm.id) > 0 ? 'حفظ التعديلات' : 'إضافة الخدمة'}
            </Button>
            {Number(serviceForm.id) > 0 ? (
              <Button type="button" variant="secondary" onClick={() => setServiceForm(emptyService())}>
                إلغاء التعديل
              </Button>
            ) : null}
          </div>
        </Form>

        {services.length === 0 ? (
          <Empty title="لا خدمات" />
        ) : (
          <ListTable headers={['الخدمة', 'التسعير', 'الرموز', 'فئات الشحن', 'الحالة', 'إجراءات']}>
            <tbody>
              {services.map((s) => (
                <tr key={str(s.id)}>
                  <td>
                    <strong>{str(s.name)}</strong>
                    <small className="muted block">{str(s.slug)}</small>
                  </td>
                  <td>{PRICING_LABELS[str(s.pricing_mode)] ?? str(s.pricing_mode)}</td>
                  <td>{str(s.codes_count, '0')}</td>
                  <td>{str(s.packages_count, '0')}</td>
                  <td>
                    <StatusBadge status={Number(s.is_active) === 1 ? 'active' : 'blocked'} />
                  </td>
                  <td>
                    <div className="table-actions">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          setServiceForm({
                            id: s.id,
                            name: s.name,
                            slug: s.slug,
                            pricing_mode: s.pricing_mode,
                            is_active: s.is_active,
                            sort_order: s.sort_order,
                          })
                        }
                      >
                        تعديل
                      </Button>
                      {Number(s.is_system) !== 1 ? (
                        <Button type="button" variant="danger" onClick={() => void deleteService(s)}>
                          حذف
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
        )}
      </SectionCard>

      <SectionCard
        title="رموز تنفيذ USSD"
        subtitle="حدد رمز التنفيذ المعتمد لكل شبكة وخدمة، ويمكنك تثبيت كل رمز على جهاز تنفيذ محدد أو تركه لأي جهاز جاهز."
        icon={<Icon name="device" size={24} />}
      >
        <div className="ussd-code-network-tabs choice-row">
          <button type="button" className={companyTab === 'syriatel' ? 'active' : ''} onClick={() => setCompanyTab('syriatel')}>
            سيريتل
          </button>
          <button type="button" className={companyTab === 'mtn' ? 'active' : ''} onClick={() => setCompanyTab('mtn')}>
            MTN
          </button>
        </div>
        <div className="ussd-template-help card-meta">
          <code>{'{phone}'}</code> رقم المستفيد · <code>{'{amount}'}</code> القيمة · <code>{'{pin}'}</code> رمز الشريحة
        </div>

        <div className="ussd-code-grid top-space">
          {activeServices.map((svc) => {
            const slug = str(svc.slug);
            const group = groupKey(companyTab, slug);
            const groupCodes = codes.filter(
              (c) => str(c.company) === companyTab && str(c.operation_kind) === slug,
            );
            const selectedId = selectedCodeId[group] ?? groupCodes.find((c) => Number(c.is_default) === 1)?.id ?? groupCodes[0]?.id;
            const current = groupCodes.find((c) => Number(c.id) === Number(selectedId));
            const draft = current ? draftFor(Number(current.id), current) : null;

            return (
              <article key={slug} className="ussd-code-card section-card">
                <div className="list-head">
                  <div>
                    <strong>{str(svc.name)}</strong>
                    <small className="muted">{PRICING_LABELS[str(svc.pricing_mode)] ?? ''}</small>
                  </div>
                  {current ? (
                    <StatusBadge
                      status={
                        Number(current.is_default) === 1
                          ? Number(current.is_active) === 1
                            ? 'approved'
                            : 'blocked'
                          : Number(current.is_active) === 1
                            ? 'active'
                            : 'blocked'
                      }
                    />
                  ) : null}
                </div>

                {!current ? (
                  <Empty title="لا رمز" hint="أضف رمزاً جديداً من الأسفل." />
                ) : (
                  <>
                    {groupCodes.length > 1 ? (
                      <Select
                        label="الكود المحفوظ"
                        value={String(selectedId ?? '')}
                        onChange={(v) => setSelectedCodeId((m) => ({ ...m, [group]: Number(v) }))}
                        options={groupCodes.map((c) => ({
                          value: String(c.id),
                          label: `${str(c.name)}${Number(c.is_default) === 1 ? ' — المعتمد' : ''}`,
                        }))}
                      />
                    ) : null}
                    {draft && Number(draft.is_system) !== 1 ? (
                      <Input
                        label="الاسم"
                        value={str(draft.name, '')}
                        onChange={(e) => patchDraft(Number(current.id), current, { name: e.target.value })}
                        placeholder="مثال: الكود الجديد"
                      />
                    ) : null}
                    <Input
                      label="رمز USSD"
                      value={str(draft?.code_template, '')}
                      onChange={(e) => patchDraft(Number(current.id), current, { code_template: e.target.value })}
                      placeholder="*...#"
                    />
                    <Select
                      label="جهاز التنفيذ"
                      value={String(Number(draft?.executor_device_id) || 0)}
                      onChange={(v) =>
                        patchDraft(Number(current.id), current, { executor_device_id: Number(v) || 0 })
                      }
                      options={[
                        { value: '0', label: 'أي جهاز جاهز' },
                        ...devices.map((d) => ({
                          value: String(d.id),
                          label: `${str(d.device_name ?? d.name)}${Number(d.is_online) === 1 ? ' — متصل' : ''}`,
                        })),
                      ]}
                    />
                    <Select
                      label="الحالة"
                      value={String(Number(draft?.is_active ?? 1))}
                      onChange={(v) => patchDraft(Number(current.id), current, { is_active: Number(v) })}
                      options={[
                        { value: '1', label: 'مفعّل' },
                        { value: '0', label: 'معطّل' },
                      ]}
                    />
                    <div className="action-row wrap">
                      <Button type="button" disabled={busy} onClick={() => void saveCode(Number(current.id), current)}>
                        حفظ
                      </Button>
                      {Number(current.is_default) !== 1 ? (
                        <Button type="button" variant="secondary" disabled={busy} onClick={() => void saveCode(Number(current.id), current, true)}>
                          اعتماد
                        </Button>
                      ) : null}
                      {Number(current.is_system) !== 1 ? (
                        <Button type="button" variant="danger" onClick={() => void deleteCode(current)}>
                          حذف
                        </Button>
                      ) : null}
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </div>

        <SectionCard title="إضافة رمز جديد" icon={<Icon name="package" size={20} />}>
          <Form onSubmit={() => void addCode()}>
            <Select
              label="الخدمة"
              value={newCode.operation_kind || str(activeServices[0]?.slug, '')}
              onChange={(v) => setNewCode((c) => ({ ...c, operation_kind: v }))}
              options={activeServices.map((s) => ({ value: str(s.slug), label: str(s.name) }))}
            />
            <Input label="الاسم" value={newCode.name} onChange={(e) => setNewCode((c) => ({ ...c, name: e.target.value }))} />
            <Input
              label="رمز USSD"
              value={newCode.code_template}
              onChange={(e) => setNewCode((c) => ({ ...c, code_template: e.target.value }))}
            />
            <Button type="submit" disabled={busy}>
              إضافة رمز ({COMPANY_LABELS[companyTab] ?? companyTab})
            </Button>
          </Form>
        </SectionCard>
      </SectionCard>
    </>
  );
}
